import { GrowthBook } from "@growthbook/growthbook";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { usePrevious } from "@mantine/hooks";
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSegmentContext } from "@/context/segment.context";
import type { Company, User } from "@/types/user";
import type { Nullable } from "@/types/utils";

import { useUserContext } from "@/context/user.context";
import type { AppFeatures } from "@/types/features.generated";

export const GrowthBookContextProvider = ({ children }: PropsWithChildren) => {
  // Using raw segment analytics rather than our analytics context because GB
  // events don't need the additional defaults added in AnalyticsContext
  const { analytics } = useSegmentContext();
  const { company, initialized, isInternal, user } = useUserContext();
  const previousInitialized = usePrevious(initialized);
  const [gbInitialized, setGBInitialized] = useState(false);

  const gb = useMemo(
    () =>
      new GrowthBook<AppFeatures>({
        apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST,
        clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
        enableDevMode:
          process.env.NODE_ENV === "development" ||
          // Enable for staging
          process.env.VERCEL_ENV === "preview",
      }),
    [],
  );

  const setAttributes = useCallback(
    (company: Nullable<Company>, user: Nullable<User>, isInternal: boolean) =>
      gb.setAttributes({
        companyId: company?.id ?? null,
        email: user?.email ?? null,
        id: user?.id ?? null,
        isInternal,
      }),
    [gb],
  );

  useEffect(() => {
    // On initial renders we won't yet have user data for use in targeting
    // attributes. Wait for user context initialization before initializing GB
    if (initialized && !previousInitialized) {
      // We set tracking her rather than within the `gb` memo because `useMemo`
      // (unlike `useEffect`) will fire during SSR. As segment is only available
      // in the browser, this would throw an error.
      gb.setTrackingCallback((experiment, result) => {
        analytics.track("Experiment Viewed", {
          experimentId: experiment.key,
          variationId: result.key,
        });
      });

      setAttributes(company, user, isInternal)
        .then(() => gb.init({ streaming: true, timeout: 1000 }))
        .then(() => setGBInitialized(true))
        .catch((error) => {
          if (error) {
            window?.newrelic?.noticeError(error, {
              // We add a custom message because, by default, the error will
              // have a message of "HTTP error: <CODE>", with no indication that
              // it originated during GrowthBook initialization.
              customMessage: `Error during GrowthBook initialization: ${error.message}`,
            });
          }
        });
    }
  }, [
    analytics,
    company,
    gb,
    initialized,
    isInternal,
    previousInitialized,
    setAttributes,
    user,
  ]);

  // This effect serves to keep targeting attributes up to date after
  // initialization (log in/out, user profile changes, etc)
  useEffect(() => {
    if (gbInitialized) {
      setAttributes(company, user, isInternal);
    }
  }, [company, gbInitialized, isInternal, setAttributes, user]);

  return <GrowthBookProvider growthbook={gb}>{children}</GrowthBookProvider>;
};
